import { createSlice } from "@reduxjs/toolkit";

/**
 * Vendor management state.
 *
 * Pagination and filtering are now server-side — the API receives page/size/name/cityId
 * as query params and returns only the current page's data.  The slice stores the
 * current filter/page values; useGetVendors reads them and includes them in the
 * TanStack Query key so a param change automatically triggers a refetch.
 */
const vendorSlice = createSlice({
    name: "vendor",
    initialState: {
        vendors: [], // current page's results from the API
        count: 0, // total matching vendors (from API)
        pages: 1, // total pages (from API)
        currentPage: 1,
        size: 10,
        nameFilter: "",
        cityFilter: "",
        // "" (any) | "closed" | "open" — whether the restaurant's app has been
        // seen recently. Server-side, because the list paginates.
        appStatusFilter: "",
        dashboardStatusFilter: "",
        // How the table relates to the customer's order. Server-side, because
        // the list paginates.
        //   ""         the admin's own table: `id`, or the sort he chose
        //   "customer" the list as a customer sees it now (open first, then
        //              the pinned ones, then the 30-minute rotation)
        //   "pinned"   only the pinned restaurants, in the order of their place
        orderView: "",
        sortBy: "",
        sortDir: "asc",
    },
    reducers: {
        /**
         * Called by useGetVendors after a successful fetch.
         * @param {{ results: object[], count: number, pages: number }} payload
         */
        setVendors(state, action) {
            const { results, count, pages } = action.payload;
            state.vendors = results;
            state.count = count;
            state.pages = pages;
            // Clamp current page in case filters reduced the total
            if (state.currentPage > pages) {
                state.currentPage = pages || 1;
            }
        },

        setPage(state, action) {
            state.currentPage = action.payload;
        },

        filterDateByName(state, action) {
            state.nameFilter = action.payload;
            state.currentPage = 1; // reset to first page on new search
        },

        filterDateByCity(state, action) {
            state.cityFilter = action.payload;
            state.currentPage = 1;
        },

        filterByDashboardStatus(state, action) {
            state.dashboardStatusFilter = action.payload;
            state.currentPage = 1;
        },
        // Sorting changes what "page 1" contains, so it resets the page for the
        // same reason a filter does.
        setSort(state, action) {
            state.sortBy = action.payload.sortBy;
            state.sortDir = action.payload.sortDir;
            // A sort the admin chose replaces the customer's order: the table
            // cannot be in both.
            if (state.orderView === "customer") state.orderView = "";
            state.currentPage = 1;
        },
        filterByAppStatus(state, action) {
            state.appStatusFilter = action.payload;
            state.currentPage = 1;
        },
        // «المثبّتة فقط» sorts by the place, because that view exists to read
        // the pins in order. «كما يراه الزبون» takes its order from the server,
        // so any sort is dropped. Back to «الكل», a sort that a view set is
        // dropped too.
        setOrderView(state, action) {
            state.orderView = action.payload;
            state.sortBy = action.payload === "pinned" ? "displayRank" : "";
            state.sortDir = "asc";
            state.currentPage = 1;
        },
    },
});

export const {
    setPage,
    setVendors,
    filterDateByName,
    filterDateByCity,
    filterByAppStatus,
    filterByDashboardStatus,
    setOrderView,
    setSort,
} = vendorSlice.actions;

export default vendorSlice;
